import { Person, Transaction, PersonCategory } from "@/types";
import { entriesAPI } from "@/api/entries";
import { supabase } from "@/integrations/supabase/client";

// These are just empty arrays that will be populated from Supabase
export const people: Person[] = [];
export const personTransactions: Transaction[] = [];

// Functions to load data from Supabase
export async function loadPeople() {
  try {
    const { data, error } = await supabase.from('people').select('*');
    
    if (error) {
      
      return [];
    }
    
    // Clear and refill the array
    people.length = 0;
    
    // Transform database people to match the Person interface
    const transformedPeople = (data || []).map(person => ({
      id: person.id,
      name: person.name,
      category: mapToPersonCategory(person.category),
      specialty: person.specialty || undefined,
      avatar: person.avatar || undefined,
      linked: person.linked || false
    }));
    
    people.push(...transformedPeople);
    return people;
  } catch (err) {
    
    return [];
  }
}

// Helper function to ensure the category is a valid PersonCategory
function mapToPersonCategory(category: string): PersonCategory {
  if (
    category === 'laborer' ||
    category === 'supplier' ||
    category === 'subcontractor' ||
    category === 'employer' ||
    category === 'partner' ||
    category === 'engineering' ||
    category === 'other'
  ) {
    return category as PersonCategory;
  }
  // Default to laborer if the category is not valid
  return 'laborer';
}

export async function loadPersonTransactions(personId: string) {
  try {
    // Instead of using a 'transactions' table, use the entries table with appropriate filtering
    const entries = await entriesAPI.getEntriesByPerson(personId);
    
    // Filter out shadow entries (these are currency conversions)
    const filteredEntries = entries.filter(entry => !entry.is_shadow_entry);
    
    // Get all projects to map project IDs to names
    let projectsMap = {};
    try {
      const { data: projects } = await supabase
        .from('projects')
        .select('id, name');
      
      if (projects) {
        projectsMap = projects.reduce((map, project) => {
          map[project.id] = project.name;
          return map;
        }, {});
      }
    } catch (err) {
      
    }
    
    // Transform entries to the Transaction format
    const transactions: Transaction[] = filteredEntries.map(entry => {
      // Calculate dueInDays if due_date exists
      let dueInDays: number | undefined = undefined;
      if (entry.due_date) {
        const dueDate = new Date(entry.due_date);
        const entryDate = new Date(entry.date);
        const diffTime = dueDate.getTime() - entryDate.getTime();
        dueInDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }
      
      // Determine checkType if transaction is a check
      let checkType: 'givenCheck' | 'receivedCheck' | undefined = undefined;
      if (entry.type === 'givenCheck' || entry.type === 'receivedCheck') {
        checkType = entry.type;
      }
      
      // Ensure image_url is properly mapped from any available field
      const imageUrl = entry.file_url || entry.image_url || null;
      
      
      
      return {
        id: entry.id,
        personId: entry.person_id,
        projectId: entry.project_id,
        projectName: projectsMap[entry.project_id] || entry.project_id,
        description: entry.description || '',
        amount: entry.amount || 0,
        date: new Date(entry.date),
        // Map entry.type to transactionType
        transactionType: mapEntryTypeToTransactionType(entry.type),
        // Add other fields as needed
        status: entry.status || undefined,
        image_url: imageUrl,
        checkNumber: entry.invoice_number || undefined,
        dueInDays: dueInDays,
        checkType: checkType,
        is_shadow_entry: entry.is_shadow_entry,
        
        // Legacy fields (for backward compatibility)
        usdAmount: entry.usd_amount,
        euroAmount: entry.euro_amount,
        
        // New currency fields with official/unofficial distinction
        amount_try_official: entry.amount_try_official,
        amount_try_unofficial: entry.amount_try_unofficial,
        amount_usd_official: entry.amount_usd_official,
        amount_usd_unofficial: entry.amount_usd_unofficial,
        amount_eur_official: entry.amount_eur_official,
        amount_eur_unofficial: entry.amount_eur_unofficial,
        amount_gbp_official: entry.amount_gbp_official, 
        amount_gbp_unofficial: entry.amount_gbp_unofficial,
        amount_jpy_official: entry.amount_jpy_official,
        amount_jpy_unofficial: entry.amount_jpy_unofficial,
        amount_rub_official: entry.amount_rub_official,
        amount_rub_unofficial: entry.amount_rub_unofficial,
        amount_kwd_official: entry.amount_kwd_official,
        amount_kwd_unofficial: entry.amount_kwd_unofficial,
        amount_sar_official: entry.amount_sar_official,
        amount_sar_unofficial: entry.amount_sar_unofficial,
        amount_chf_official: entry.amount_chf_official,
        amount_chf_unofficial: entry.amount_chf_unofficial,
        amount_xau_official: entry.amount_xau_official,
        amount_xau_unofficial: entry.amount_xau_unofficial,
        amount_btc_official: entry.amount_btc_official,
        amount_btc_unofficial: entry.amount_btc_unofficial,
        
        original_currency: entry.original_currency,
        original_amount: entry.original_amount,
        is_official: entry.is_official || false,

        // Payment-method model
        payment_method: entry.payment_method ?? null,
        advance_sub_method: entry.advance_sub_method ?? null,
        counts_toward_cost: entry.counts_toward_cost ?? null,
        is_settled: entry.is_settled ?? null,
        settled_method: entry.settled_method ?? null,
        settles_entry_id: entry.settles_entry_id ?? null,
      };
    });
    
    return transactions;
  } catch (err) {
    
    return [];
  }
}

// Function to delete a transaction
export async function deleteTransaction(transactionId: string) {
  try {
    // Use the entriesAPI to delete the entry
    return await entriesAPI.deleteEntry(transactionId);
  } catch (err) {
    
    throw err;
  }
}

// Helper function to map entry types to transaction types
function mapEntryTypeToTransactionType(entryType: string): 'laborCost' | 'payment' | 'check' {
  switch (entryType) {
    case 'laborCost':
      return 'laborCost';
    case 'givenCheck':
    case 'receivedCheck':
      return 'check';
    case 'expense':
    default:
      return 'payment';
  }
} 